import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUserGroup } from '../hooks/useUserGroup';
import { entryStudioPath } from '../lib/userGroups';
import { Loader2, AlertCircle, ArrowLeft, ArrowRight, Check, ImagePlus, Trash, Sparkles, Download, RefreshCw, Globe, PenLine } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { invalidateCache, CACHE_KEYS } from '../utils/sessionCache';
import { postBrandIdentity, postBrandLogo, type BrandLogoBody } from '../lib/brandBackend';
import LoadingAnimation from '../components/LoadingAnimation';
import { CustomSelect } from '../components/CustomSelect';
import { getUserPlan, planAllowsMultiBrand } from '../lib/planUtils';
import SubscriptionLimitModal from '../components/SubscriptionLimitModal';

type Step = 'choose_path' | 'web_url' | 'intro' | 'brand_info' | 'brand' | 'audience' | 'logo' | 'success';

interface FormData {
  brandDescription: string;
  targetAudience: string;
  brandName: string;
  brandRubro: string;
  websiteUrl: string;
}

const RUBROS = [
  'Moda e Indumentaria',
  'Hogar y Decoración',
  'Belleza y Cuidado Personal',
  'Tecnología',
  'Salud y Bienestar',
  'Automotriz',
  'Gastronomía',
  'Juguetería',
  'Arte y Manualidades',
  'Librería',
  'Creación de Contenido',
  'Otro',
];


export const BrandDescription = () => {
  const navigate = useNavigate();
  const { isDevTester, grupo } = useUserGroup();
  const [searchParams] = useSearchParams();
  const isAdditional = searchParams.get('mode') === 'additional';
  const [showMultiBrandRequiredModal, setShowMultiBrandRequiredModal] = useState(false);

  // Guard: si el usuario llega por URL a mode=additional sin plan pago, mostrar modal
  useEffect(() => {
    if (!isAdditional) return;
    let cancelled = false;
    (async () => {
      const plan = await getUserPlan();
      if (cancelled) return;
      if (!planAllowsMultiBrand(plan)) {
        setShowMultiBrandRequiredModal(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdditional]);

  // Safety net: si es additional y no existe una marca default en brand_profiles,
  // crearla a partir de company_profiles para que cuentas legacy no queden sin default.
  // Corre al montar (antes de snapshots y webhooks) para que el polling no la confunda
  // con la marca nueva del webhook.
  useEffect(() => {
    if (!isAdditional) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user || cancelled) return;
        const { data: profile } = await supabase
          .from('profiles')
          .select('id_company')
          .eq('id', user.id)
          .single();
        const companyId = (profile as any)?.id_company as string | undefined;
        if (!companyId || cancelled) return;

        const { data: defaultBrand } = await supabase
          .from('brand_profiles')
          .select('id')
          .eq('id_company', companyId)
          .eq('is_default', true)
          .eq('is_draft', false)
          .limit(1)
          .maybeSingle();

        if (!defaultBrand && !cancelled) {
          const { data: companyRow } = await supabase
            .from('company_profiles')
            .select('nombre, rubro, logo_url')
            .eq('id_company', companyId)
            .maybeSingle();

          const defaultName = (companyRow as any)?.nombre?.trim() || 'Marca principal';
          const defaultRubro = (companyRow as any)?.rubro || '';
          const defaultLogo = (companyRow as any)?.logo_url || null;

          await supabase.from('brand_profiles').insert({
            id_company: companyId,
            name: defaultName,
            rubro: defaultRubro,
            logo_url: defaultLogo,
            context: '',
            colors: [],
            is_default: true,
            is_draft: false,
          });

          // Backfill: asignar productos sin marca al nuevo brand_profile default
          const { data: newDefault } = await supabase
            .from('brand_profiles')
            .select('id')
            .eq('id_company', companyId)
            .eq('is_default', true)
            .limit(1)
            .maybeSingle();

          if (newDefault?.id) {
            await supabase
              .from('productos')
              .update({ brand_id: newDefault.id })
              .eq('id_company', companyId)
              .is('brand_id', null);
          }
        }
      } catch (e) {
        console.error('[safety-net] error ensuring default brand:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [isAdditional]);

  const [currentStep, setCurrentStep] = useState<Step>('choose_path');
  const [chosenPath, setChosenPath] = useState<'web' | 'manual' | null>(null);
  const [formData, setFormData] = useState<FormData>({
    brandDescription: '',
    targetAudience: '',
    brandName: '',
    brandRubro: '',
    websiteUrl: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Logo step state
  const [hasLogo, setHasLogo] = useState<boolean | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [generatingLogo, setGeneratingLogo] = useState(false);
  const [generatedLogoUrl, setGeneratedLogoUrl] = useState<string | null>(null);
  // Estado del loader "creando marca" al final del onboarding
  const [creatingBrand, setCreatingBrand] = useState(false);
  // Brand id detectado por el polling (para redirigir al editor)
  const [detectedBrandId, setDetectedBrandId] = useState<string | null>(null);
  // Frase didáctica rotativa mientras se genera el análisis
  const [loadingPhrase, setLoadingPhrase] = useState(0);
  const LOADING_PHRASES = [
    'Analizando tu marca...',
    'Estudiando tu logo...',
    'Detectando referencias visuales...',
    'Pensando en tu público objetivo...',
    'Investigando tu rubro...',
    'Definiendo la personalidad de tu marca...',
    'Buscando el tono adecuado...',
    'Detectando la paleta de colores...',
    'Probando combinaciones cromáticas...',
    'Eligiendo tipografías...',
    'Conectando colores con identidad...',
    'Calculando el posicionamiento estratégico...',
    'Armando el análisis estratégico...',
    'Hilando todos los elementos...',
    'Revisando coherencia visual...',
    'Optimizando recomendaciones...',
    'Afinando los últimos detalles...',
    'Casi listo...',
  ];
  const [creationTimedOut, setCreationTimedOut] = useState(false);
  // Error específico del webhook (network, 4xx, 5xx). Cuando se setea, mostramos UI de recuperación.
  const [webhookError, setWebhookError] = useState<null | 'network' | 'http'>(null);
  // Snapshot de brand_profiles (id → updated_at) antes del submit, para detectar cambios
  // sin depender de timestamps (los relojes cliente/servidor pueden tener drift de minutos).
  const brandsSnapshotRef = useRef<Map<string, string>>(new Map());
  // No blocking analysis state; analysis generation runs in background

  

  // Load existing data if available
  // Track if component is mounted to prevent state updates after navigation
  const [isMounted, setIsMounted] = useState(true);
  const genCreateTriggeredRef = useRef(false);

  useEffect(() => {
    // Set mounted state
    setIsMounted(true);

    // Cleanup function to set mounted state to false when component unmounts
    return () => {
      setIsMounted(false);
    };
  }, []);

  // Polling de brand_profiles: cuando el user terminó el onboarding,
  // esperamos a que la marca esté guardada en DB antes de mostrar el success.
  useEffect(() => {
    if (!creatingBrand) return;
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 30; // ~90s con poll cada 3s

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;

      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          console.warn('[brand polling] no user, retry');
        } else {
          const { data: profile } = await supabase
            .from('profiles')
            .select('id_company')
            .eq('id', user.id)
            .single();
          const idCompany = (profile as any)?.id_company as string | undefined;

          if (idCompany) {
            const { data: brands, error: brandsError } = await supabase
              .from('brand_profiles')
              .select('id, updated_at')
              .eq('id_company', idCompany);

            // Detectar cambios respecto al snapshot:
            // - id nuevo (no estaba antes) → marca additional creada
            // - id existente con updated_at distinto → marca default actualizada
            let detected: string | null = null;
            if (brands) {
              const snap = brandsSnapshotRef.current;
              for (const b of brands) {
                const id = b.id as string;
                const u = b.updated_at as string;
                if (!snap.has(id)) {
                  detected = `new:${id}`;
                  break;
                }
                if (snap.get(id) !== u) {
                  detected = `updated:${id}`;
                  break;
                }
              }
            }

            console.log('[brand polling]', {
              attempt: attempts,
              snapshotSize: brandsSnapshotRef.current.size,
              brandsNow: brands?.length,
              detected,
              error: brandsError?.message,
            });

            if (!cancelled && detected) {
              // detected viene como "new:<id>" o "updated:<id>"
              const id = detected.split(':')[1];
              if (id) setDetectedBrandId(id);
              setCreatingBrand(false);
              return;
            }
          }
        }
      } catch (e) {
        console.error('[brand polling] error:', e);
      }

      if (attempts >= maxAttempts) {
        if (!cancelled) {
          setCreatingBrand(false);
          setCreationTimedOut(true);
        }
        return;
      }

      if (!cancelled) {
        setTimeout(poll, 3000);
      }
    };

    poll();

    return () => {
      cancelled = true;
    };
  }, [creatingBrand]);

  // Rotar frases didácticas mientras se está creando la marca
  useEffect(() => {
    if (!creatingBrand) return;
    setLoadingPhrase(0);
    const interval = setInterval(() => {
      setLoadingPhrase((prev) => (prev + 1) % LOADING_PHRASES.length);
    }, 3500);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creatingBrand]);

  // Trigger analysis generation without blocking navigation and persist result when ready
  // When forceBypass is true, skip the lock mechanism (used when completing the brand flow with a logo)
  const triggerMarketingAnalysisInBackground = async (logoUrl?: string, forceBypass = false) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Resolve id_company to update config later
      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();
      const companyId = (profile as any)?.id_company as string | undefined;

      // Fire the webhook request (do not await for navigation purposes)
      let shouldTrigger = forceBypass; // Bypass lock if explicitly requested
      if (!forceBypass) {
        try {
          const LOCK_KEY = 'atlas:identity:gen:lock';
          const LOCK_TTL_MS = 15 * 60 * 1000;
          if (!genCreateTriggeredRef.current) {
            const lastStr = localStorage.getItem(LOCK_KEY);
            const last = lastStr ? parseInt(lastStr, 10) : 0;
            const now = Date.now();
            shouldTrigger = !last || (now - last > LOCK_TTL_MS);
            if (shouldTrigger) {
              localStorage.setItem(LOCK_KEY, String(now));
              genCreateTriggeredRef.current = true;
            }
          }
        } catch (_e) {
          if (!genCreateTriggeredRef.current) {
            genCreateTriggeredRef.current = true;
            shouldTrigger = true;
          }
        }
      }

      if (!shouldTrigger) return;

      const requestBody: {
        user_id: string;
        mode: string;
        logo_url?: string;
        is_default_brand?: boolean;
        nombre_brand?: string;
        rubro_brand?: string;
        description_brand?: string;
        target_public_brand?: string;
        website_url?: string;
      } = {
        user_id: user.id,
        mode: 'create',
      };
      if (logoUrl) {
        requestBody.logo_url = logoUrl;
      }
      // Si el usuario eligió el flujo web, mandamos la URL para que n8n haga scraping.
      // El backend usa esto como señal de cuál rama tomar (URL vs descripción manual).
      if (chosenPath === 'web' && formData.websiteUrl.trim()) {
        const raw = formData.websiteUrl.trim();
        // Normalizar: agregar https:// si no tiene protocolo
        const normalized = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
        requestBody.website_url = normalized;
      }
      // Multi-marca: cuando es additional, mandamos los datos propios de la nueva marca
      // (sin tocar la config legacy de la marca default).
      if (isAdditional) {
        requestBody.is_default_brand = false;
        if (formData.brandName.trim()) {
          requestBody.nombre_brand = formData.brandName.trim();
        }
        if (formData.brandRubro.trim()) {
          requestBody.rubro_brand = formData.brandRubro.trim();
        }
        if (formData.brandDescription.trim()) {
          requestBody.description_brand = formData.brandDescription.trim();
        }
        if (formData.targetAudience.trim()) {
          requestBody.target_public_brand = formData.targetAudience.trim();
        }
      }

      // Flag ON → atlas /v1/brand/identity (JWT, sin user_id; devuelve el
      // análisis real en {response} — antes n8n venía vacío y se guardaba '').
      // Flag OFF → webhook n8n byte-idéntico. ADR-598.
      postBrandIdentity(requestBody)
        .then(async (response) => {
          if (!response.ok) {
            console.error('[webhook generar_identidad] non-OK response', response.status);
            setWebhookError('http');
            setCreatingBrand(false);
            return;
          }
          let data: any = null;
          try {
            data = await response.json();
          } catch (_e) {
            // ignore json parse errors
          }
          if (!companyId) return;
          const analysis = data?.response || '';
          try {
            await supabase
              .from('config')
              .update({ media_agent_analysis: analysis })
              .eq('id_company', companyId);
            // El banner "Mejorá tus fotos" se gatea por
            // `config.media_agent_analysis` (cacheado in-memory 5 min en
            // useHomeBanners). Invalidamos ese cache para que el banner se
            // esconda AL INSTANTE tras completar el análisis — sino seguía
            // apareciendo hasta el TTL (5 min) o un reload.
            invalidateCache(CACHE_KEYS.COMPANY_CONFIG);
          } catch (e) {
            console.error('Error saving background marketing analysis:', e);
          }
        })
        .catch((err) => {
          console.error('Background marketing analysis generation failed:', err);
          setWebhookError('network');
          setCreatingBrand(false);
        });
    } catch (e) {
      console.error('Error preparing background marketing analysis:', e);
    }
  };

  useEffect(() => {
    const fetchExistingData = async () => {
      try {
        if (!isMounted) return;
        
        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        if (!user || !isMounted) return;

        // Get user's id_company
        const { data: profile } = await supabase
          .from('profiles')
          .select('id_company')
          .eq('id', user.id)
          .single();

        if (!(profile as any)?.id_company || !isMounted) return;
        
        // Get existing config data
        const { data: configData } = await supabase
          .from('config')
          .select('media_agent_user_description, media_agent_style, media_agent_public, media_agent_objective, media_agent_analysis')
          .eq('id_company', (profile as any).id_company)
          .maybeSingle();
        
        if (configData && isMounted && !isAdditional) {
          // En modo "additional" el user está creando una marca nueva → no pre-cargamos
          // la descripción de la marca actual; empieza en blanco.
          setFormData((prev) => ({
            ...prev,
            brandDescription: configData.media_agent_user_description || '',
            targetAudience: configData.media_agent_public || '',
          }));

          // Do not auto-redirect or auto-advance steps. Intro remains as default.
        }
      } catch (err) {
        console.error('Error fetching existing data:', err);
        if (isMounted) {
          setError('Error al cargar los datos existentes');
        }
      }
    };

    fetchExistingData();
  }, [isMounted, navigate]);

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const isValidUrl = (raw: string): boolean => {
    const value = raw.trim();
    if (!value) return false;
    try {
      // Acepta con o sin protocolo; normalizamos agregando https:// si falta
      const candidate = /^https?:\/\//i.test(value) ? value : `https://${value}`;
      const u = new URL(candidate);
      // Tiene que tener un host con un punto (al menos un dominio + tld)
      return !!u.hostname && u.hostname.includes('.');
    } catch {
      return false;
    }
  };

  const validateCurrentStep = (): boolean => {
    switch (currentStep) {
      case 'choose_path':
        if (!chosenPath) {
          setError('Elegí una opción para continuar');
          return false;
        }
        break;
      case 'web_url':
        if (!isValidUrl(formData.websiteUrl)) {
          setError('Ingresá una URL válida (ej. miempresa.com)');
          return false;
        }
        break;
      case 'intro':
        // No validation needed in the intro screen
        break;
      case 'brand_info':
        if (!formData.brandName.trim()) {
          setError('Por favor, ingresa el nombre de la marca');
          return false;
        }
        if (!formData.brandRubro.trim()) {
          setError('Por favor, elegí un rubro');
          return false;
        }
        break;
      case 'brand':
        if (!formData.brandDescription.trim()) {
          setError('Por favor, ingresa una descripción de tu marca');
          return false;
        }
        break;
      case 'audience':
        if (!formData.targetAudience.trim()) {
          setError('Por favor, describe tu público objetivo');
          return false;
        }
        break;
    }
    return true;
  };

  const saveCurrentStep = async (): Promise<boolean> => {
    // Intro step does not persist anything
    if (currentStep === 'intro') return true;
    // choose_path y web_url se guardan solo en formData; los datos van directo al webhook
    if (currentStep === 'choose_path' || currentStep === 'web_url') return true;
    // brand_info se guarda solo en formData; los datos van directo al webhook (no a config)
    if (currentStep === 'brand_info') return true;
    // En additional mode no escribimos a config.media_agent_* porque eso pisaría
    // los datos de la marca default. La nueva marca persiste vía el webhook → brand_profiles.
    if (isAdditional && (currentStep === 'brand' || currentStep === 'audience')) {
      return true;
    }
    try {
      setLoading(true);
      setError(null);

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      // Get user's id_company
      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      if (!(profile as any)?.id_company) throw new Error('No company associated with user');

      // Determine which field to update based on current step
      let updateData = {} as Record<string, any>;
      switch (currentStep) {
        case 'brand':
          updateData = { media_agent_user_description: formData.brandDescription };
          break;
        case 'audience':
          updateData = { media_agent_public: formData.targetAudience };
          break;
      }
      
      // Update the config table
      const { error: updateError } = await supabase
        .from('config')
        .update(updateData)
        .eq('id_company', (profile as any).id_company);
      
      if (updateError) throw updateError;
      
      return true;
    } catch (err) {
      console.error(`Error saving ${currentStep}:`, err);
      setError(err instanceof Error ? err.message : `Error al guardar ${getStepName(currentStep)}`);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // generateMarketingAnalysis function removed in favor of background trigger without blocking UI

  // Marketing plan approval is now handled in the MarketingPlanReview component

  // Marketing analysis is now saved directly in the generateMarketingAnalysis function

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) return;
    const file = e.target.files[0];
    
    if (!file.type.startsWith('image/')) {
      setError('Por favor selecciona un archivo de imagen válido');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('El archivo es demasiado grande. El tamaño máximo es 10MB');
      return;
    }
    
    setLogoFile(file);
    setLogoPreviewUrl(URL.createObjectURL(file));
    setError(null);
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreviewUrl(null);
    setGeneratedLogoUrl(null);
  };

  const handleGenerateLogo = async () => {
    try {
      setGeneratingLogo(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      // Le pasamos el contexto de la marca que el user está creando ahora.
      // Sin esto, el workflow n8n caía a leer company_profiles + config legacy
      // (data de la marca default vieja → logos incorrectos en multi-brand).
      const logoBody: BrandLogoBody = {
        user_id: user.id,
        is_default_brand: !isAdditional,
      };
      if (formData.brandName.trim()) logoBody.brand_name = formData.brandName.trim();
      if (formData.brandRubro.trim()) logoBody.brand_rubro = formData.brandRubro.trim();
      if (formData.brandDescription.trim()) logoBody.brand_description = formData.brandDescription.trim();
      if (formData.targetAudience.trim()) logoBody.brand_target_public = formData.targetAudience.trim();
      if (chosenPath === 'web' && formData.websiteUrl.trim()) {
        const raw = formData.websiteUrl.trim();
        logoBody.website_url = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
      }

      // Flag ON → atlas /v1/brand/logo (JWT; sin créditos = 402 con copy de
      // saldo, antes n8n se colgaba sin responder). OFF → n8n. ADR-598.
      const logoUrlGenerada = await postBrandLogo(logoBody);
      setGeneratedLogoUrl(logoUrlGenerada);
      setLogoPreviewUrl(logoUrlGenerada);
    } catch (err) {
      console.error('Error generating logo:', err);
      setError(err instanceof Error ? err.message : 'Error al generar el logo');
    } finally {
      setGeneratingLogo(false);
    }
  };

  const saveLogoToStorage = async (): Promise<string | null> => {
    if (!logoFile) return null; // No logo to save
    
    try {
      setUploadingLogo(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      // Upload logo to storage in logos folder
      const timestamp = Date.now();
      const fileExt = logoFile.name.split('.').pop();
      const fileName = `logos/${timestamp}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('system')
        .upload(fileName, logoFile);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('system')
        .getPublicUrl(fileName);

      return publicUrl;
    } catch (err) {
      console.error('Error saving logo:', err);
      setError(err instanceof Error ? err.message : 'Error al guardar el logo');
      return null;
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleNext = async () => {
    if (!validateCurrentStep()) return;
    
    const saved = await saveCurrentStep();
    if (!saved) return;
    
    // Move to next step
    switch (currentStep) {
      case 'choose_path':
        // Si el user eligió web, saltamos directo al input de URL.
        // Si eligió manual, mantenemos el flujo histórico.
        if (chosenPath === 'web') {
          setCurrentStep('web_url');
        } else {
          // manual
          setCurrentStep(isAdditional ? 'brand_info' : 'intro');
        }
        break;
      case 'web_url': {
        // En flujo web NO pedimos logo en onboarding — el analisis n8n intenta
        // detectarlo via favicon del scrape. Si no lo encuentra, el usuario lo
        // sube/edita en /revisar-marca al final.
        try {
          const { data: { user: snapUser } } = await supabase.auth.getUser();
          if (snapUser) {
            const { data: snapProfile } = await supabase
              .from('profiles')
              .select('id_company')
              .eq('id', snapUser.id)
              .single();
            const snapIdCompany = (snapProfile as any)?.id_company as string | undefined;
            if (snapIdCompany) {
              const { data: snapBrands } = await supabase
                .from('brand_profiles')
                .select('id, updated_at')
                .eq('id_company', snapIdCompany);
              const snap = new Map<string, string>();
              (snapBrands || []).forEach((b: any) => {
                if (b.id && b.updated_at) snap.set(b.id, b.updated_at);
              });
              brandsSnapshotRef.current = snap;
            }
          }
        } catch (e) {
          console.error('[brand snapshot] error capturing:', e);
        }

        setCreationTimedOut(false);
        setWebhookError(null);
        setCreatingBrand(true);

        // Trigger analysis sin logo — el favicon detectado por scrape va a ser el logo_url
        triggerMarketingAnalysisInBackground(undefined, true);
        setCurrentStep('success');
        break;
      }
      case 'intro':
        setCurrentStep(isAdditional ? 'brand_info' : 'brand');
        break;
      case 'brand_info':
        setCurrentStep('brand');
        break;
      case 'brand':
        setCurrentStep('audience');
        break;
      case 'audience':
        setCurrentStep('logo');
        break;
      case 'logo':
        // Upload logo if provided and get URL, or use generated logo URL
        let finalLogoUrl: string | undefined;

        if (logoFile) {
          // User uploaded a logo file
          const uploadedLogoUrl = await saveLogoToStorage();
          if (!uploadedLogoUrl) return; // Upload failed
          finalLogoUrl = uploadedLogoUrl;
        } else if (generatedLogoUrl) {
          // User generated a logo with AI
          finalLogoUrl = generatedLogoUrl;
        }

        // Snapshot de brand_profiles ANTES del trigger, para detectar el cambio en el polling
        // (robusto a clock drift cliente/servidor)
        try {
          const { data: { user: snapUser } } = await supabase.auth.getUser();
          if (snapUser) {
            const { data: snapProfile } = await supabase
              .from('profiles')
              .select('id_company')
              .eq('id', snapUser.id)
              .single();
            const snapIdCompany = (snapProfile as any)?.id_company as string | undefined;
            if (snapIdCompany) {
              const { data: snapBrands } = await supabase
                .from('brand_profiles')
                .select('id, updated_at')
                .eq('id_company', snapIdCompany);
              const snap = new Map<string, string>();
              (snapBrands || []).forEach((b: any) => {
                if (b.id && b.updated_at) snap.set(b.id, b.updated_at);
              });
              brandsSnapshotRef.current = snap;
              console.log('[brand snapshot] captured', { size: snap.size });
            }
          }
        } catch (e) {
          console.error('[brand snapshot] error capturing:', e);
        }

        setCreationTimedOut(false);
        setWebhookError(null);
        setCreatingBrand(true);

        // Trigger analysis in background with logo URL, bypassing lock since user explicitly completed the flow
        triggerMarketingAnalysisInBackground(finalLogoUrl, true);
        setCurrentStep('success');
        break;
      case 'success':
        // Si detectamos la marca creada (con is_draft=true esperado), vamos al editor
        // para que el usuario revise/edite antes de confirmar. Si no, fallback al home.
        if (detectedBrandId) {
          navigate(`/onboarding/revisar-marca/${detectedBrandId}`, {
            replace: true,
            state: { fromOnboarding: true },
          });
        } else {
          navigate(entryStudioPath(isDevTester, grupo), {
            replace: true,
            state: { hideBrandBanner: true },
          });
        }
        break;
    }
  };

  const handleBack = () => {
    switch (currentStep) {
      case 'web_url':
        setCurrentStep('choose_path');
        break;
      case 'intro':
        // Si elegimos manual, volvemos al choose_path
        setCurrentStep('choose_path');
        break;
      case 'brand_info':
        // Para additional, brand_info viene después de choose_path (saltando intro).
        setCurrentStep('choose_path');
        break;
      case 'brand':
        setCurrentStep(isAdditional ? 'brand_info' : 'intro');
        break;
      case 'audience':
        setCurrentStep('brand');
        break;
      case 'logo':
        // En flujo web no hay pregunta Sí/No (siempre mostramos upload), así que
        // el back va directo a web_url
        if (chosenPath === 'web') {
          setHasLogo(null);
          setLogoFile(null);
          setLogoPreviewUrl(null);
          setCurrentStep('web_url');
          break;
        }
        // Manual: si ya seleccionó Sí o No, volvemos a la pregunta. Si está en la pregunta,
        // volvemos a audience.
        if (hasLogo !== null) {
          setHasLogo(null);
          setLogoFile(null);
          setLogoPreviewUrl(null);
        } else {
          setCurrentStep('audience');
        }
        break;
    }
    setError(null);
  };

  // Reset de estado del flow de creación + ir a un step específico (para recovery)
  const resetCreationAndGoTo = (targetStep: Step) => {
    setCreatingBrand(false);
    setCreationTimedOut(false);
    setWebhookError(null);
    setDetectedBrandId(null);
    genCreateTriggeredRef.current = false;
    try {
      localStorage.removeItem('atlas:identity:gen:lock');
    } catch (_e) {
      // ignore
    }
    setError(null);
    setCurrentStep(targetStep);
  };

  const getStepName = (step: Step): string => {
    switch (step) {
      case 'choose_path': return 'Cómo empezar';
      case 'web_url': return 'URL de tu web';
      case 'intro': return 'Introducción';
      case 'brand_info': return 'Datos de la marca';
      case 'brand': return 'Descripción de marca';
      case 'audience': return 'Público objetivo';
      case 'logo': return 'Logo';
      case 'success': return 'Éxito';
    }
  };


  // Titles and descriptions to show OUTSIDE the white container
  const stepTexts = (() => {
    switch (currentStep) {
      case 'choose_path':
        return isAdditional
          ? { title: '¿Cómo querés configurar tu nueva marca?', subtitle: 'Elegí la opción que más se acerque a tu situación.' }
          : { title: '¡Vamos a conocernos!', subtitle: 'Para entender tu marca y generar contenido a su estilo, elegí cómo querés arrancar.' };
      case 'web_url':
        return { title: 'Pegá la URL de tu sitio web', subtitle: 'La IA va a analizar tu web para entender tu marca, productos y estilo.' };
      case 'intro':
        return isAdditional
          ? { title: 'Agregá una nueva marca', subtitle: 'Vamos a configurar los datos de tu nueva marca para que la IA pueda generar contenido específico para ella.' }
          : { title: '¡Vamos a conocernos!', subtitle: 'Hola, soy la IA de Atlas, el asistente virtual de marketing que te va a ayudar a que tu marca alcance el siguiente nivel.' };
      case 'brand_info':
        return { title: '¿Cómo se llama esta marca?', subtitle: 'Necesitamos un nombre y rubro propios para diferenciarla de tu marca principal.' };
      case 'brand':
        return isAdditional
          ? { title: 'Contame sobre tu nueva marca', subtitle: 'Describila brevemente y decime qué la hace diferente.' }
          : { title: 'Contame un poco sobre tu marca', subtitle: 'Describimela brevemente y decime qué es lo que la hace diferente.' };
      case 'audience':
        return { title: '¿A quién querés llegar?', subtitle: 'Contame a qué tipo de personas apuntás: edades, intereses y gustos.' };
      case 'logo':
        if (hasLogo === false) {
          return { title: '¿Te gustaría tener un logo profesional?', subtitle: '' };
        } else if (hasLogo === true) {
          return { title: 'Subí tu logo', subtitle: 'Si subís una foto de tu logo, podes añadirlo a las fotos que generes.' };
        }
        return isAdditional
          ? { title: '¿Esta marca tiene un logo?', subtitle: '' }
          : { title: '¿Tu marca tiene un logo?', subtitle: '' };
      case 'success':
        if (creatingBrand) {
          return isAdditional
            ? { title: 'Creando tu nueva marca', subtitle: 'Estamos generando el análisis, paleta y tipografía. Esto puede tomar hasta 90 segundos.' }
            : { title: 'Procesando tu marca', subtitle: 'Estamos generando el análisis y la paleta. Esto puede tomar hasta 90 segundos.' };
        }
        if (webhookError || creationTimedOut) {
          if (chosenPath === 'web') {
            return {
              title: 'No pudimos analizar tu web',
              subtitle: 'Puede ser que el sitio esté caído o nos haya bloqueado. Podés reintentar con la URL o cargar los datos manualmente.',
            };
          }
          return {
            title: 'Algo falló al crear la marca',
            subtitle: 'No pudimos procesar el análisis. Podés volver a intentarlo desde el inicio.',
          };
        }
        return isAdditional
          ? { title: 'Marca creada', subtitle: 'Tu nueva marca quedó registrada. Ya podés generar contenido específico para ella.' }
          : { title: 'Fotos mejoradas', subtitle: 'Ahora la IA entenderá tu marca y hará fotos más acorde a tu estilo.' };
    }
  })();

  const renderStepContent = () => {
    switch (currentStep) {
      case 'choose_path':
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {/* Card Web */}
            <button
              type="button"
              onClick={() => { setChosenPath('web'); setError(null); }}
              className={`relative text-left p-4 sm:p-5 rounded-xl border-2 transition-all ${
                chosenPath === 'web'
                  ? 'border-brand-primary bg-brand-primary/5 shadow-md'
                  : 'border-neutral-200 bg-white hover:border-neutral-300 hover:bg-neutral-50'
              }`}
            >
              {chosenPath === 'web' && (
                <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-brand-primary flex items-center justify-center">
                  <Check className="w-3 h-3 text-white" strokeWidth={3} />
                </div>
              )}
              <div className="w-10 h-10 rounded-lg bg-brand-primary/10 flex items-center justify-center mb-3">
                <Globe className="w-5 h-5 text-brand-primary" />
              </div>
              <div className="text-[15px] font-semibold text-brand-black/90 mb-1">
                Tengo página web
              </div>
              <div className="text-[12px] sm:text-[13px] text-neutral-500 leading-snug">
                La IA va a analizar tu web automáticamente. Ideal si ya tenés tu marca armada online.
              </div>
            </button>

            {/* Card Manual */}
            <button
              type="button"
              onClick={() => { setChosenPath('manual'); setError(null); }}
              className={`relative text-left p-4 sm:p-5 rounded-xl border-2 transition-all ${
                chosenPath === 'manual'
                  ? 'border-brand-primary bg-brand-primary/5 shadow-md'
                  : 'border-neutral-200 bg-white hover:border-neutral-300 hover:bg-neutral-50'
              }`}
            >
              {chosenPath === 'manual' && (
                <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-brand-primary flex items-center justify-center">
                  <Check className="w-3 h-3 text-white" strokeWidth={3} />
                </div>
              )}
              <div className="w-10 h-10 rounded-lg bg-brand-primary/10 flex items-center justify-center mb-3">
                <PenLine className="w-5 h-5 text-brand-primary" />
              </div>
              <div className="text-[15px] font-semibold text-brand-black/90 mb-1">
                No tengo web, te cuento yo
              </div>
              <div className="text-[12px] sm:text-[13px] text-neutral-500 leading-snug">
                Vamos paso a paso. Te voy a preguntar sobre tu marca, público y estilo.
              </div>
            </button>
          </div>
        );
      case 'web_url':
        return (
          <div className="space-y-3">
            <input
              type="text"
              inputMode="url"
              value={formData.websiteUrl}
              onChange={(e) => handleInputChange('websiteUrl', e.target.value)}
              placeholder="https://miempresa.com"
              className="w-full px-3 h-11 rounded-md bg-surface-card border border-neutral-200 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary text-[15px] text-brand-black placeholder:text-neutral-400 transition-colors"
              autoFocus
              autoComplete="off"
              spellCheck={false}
            />
            <p className="text-[12px] text-neutral-500 leading-snug">
              Pegá la URL completa de tu página principal. Si tu marca tiene tienda online, mejor pasame esa.
            </p>
          </div>
        );
      case 'intro':
        return (
          <div className="space-y-3 text-center">
            <p className="text-[15px] text-brand-black/60">
              {isAdditional
                ? '¿Me contás sobre tu nueva marca? Necesito sus datos para generar contenido específico para ella, sin afectar la que ya tenés configurada.'
                : '¿Me contarías un poco sobre tu marca? Necesito conocerla para poder crear anuncios personalizados, que se sientan propios de tu marca.'}
            </p>
          </div>
        );
      case 'brand_info':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-[13px] font-medium text-brand-black/80 mb-1.5">
                Nombre de la marca
              </label>
              <input
                type="text"
                value={formData.brandName}
                onChange={(e) => handleInputChange('brandName', e.target.value)}
                placeholder="Ej: Linaje Atelier"
                className="w-full px-3 h-10 rounded-md bg-surface-card border border-neutral-200 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary text-[15px] text-brand-black placeholder:text-neutral-400 transition-colors"
                maxLength={80}
                autoFocus
              />
            </div>
            <div>
              <label className="block text-[13px] font-medium text-brand-black/80 mb-1.5">
                Rubro
              </label>
              <CustomSelect
                value={formData.brandRubro}
                onChange={(value) => handleInputChange('brandRubro', value)}
                options={RUBROS.map((r) => ({ value: r, label: r }))}
                placeholder="Seleccionar rubro"
              />
            </div>
          </div>
        );
      case 'brand':
        return (
          <div className="space-y-4">
            <textarea
              value={formData.brandDescription}
              onChange={(e) => handleInputChange('brandDescription', e.target.value)}
              placeholder="Describe tu marca, productos, servicios, valores..."
              className="w-full p-3 rounded-md bg-white border border-neutral-200 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary resize-none min-h-[150px] text-[15px] text-brand-black placeholder:text-neutral-400"
              rows={6}
            />
          </div>
        );
      case 'audience':
        return (
          <div className="space-y-4">
            <textarea
              value={formData.targetAudience}
              onChange={(e) => handleInputChange('targetAudience', e.target.value)}
              placeholder="Describe a quién va dirigido tu producto o servicio, edad, intereses, comportamientos..."
              className="w-full p-3 rounded-md bg-white border border-neutral-200 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary resize-none min-h-[150px] text-[15px] text-brand-black placeholder:text-neutral-400"
              rows={6}
            />
          </div>
        );
      case 'logo':
        return (
          <div className="space-y-4">
            {/* Si/No buttons (solo flujo manual; en web salteamos esta pregunta — la
                generación con IA no tiene contexto suficiente hasta que se analiza la URL,
                así que solo permitimos subir o saltear) */}
            {hasLogo === null && chosenPath !== 'web' && (
              <div className="flex flex-col gap-3 items-center pt-6">
                <button
                  type="button"
                  onClick={() => setHasLogo(true)}
                  className="w-full max-w-[200px] h-10 rounded-md border border-neutral-200 bg-surface-card text-brand-black/90 text-[15px] font-medium hover:bg-neutral-50 transition-colors shadow-sm"
                >
                  Sí
                </button>
                <button
                  type="button"
                  onClick={() => setHasLogo(false)}
                  className="w-full max-w-[200px] h-10 rounded-md border border-neutral-200 bg-surface-card text-brand-black/90 text-[15px] font-medium hover:bg-neutral-50 transition-colors shadow-sm"
                >
                  No
                </button>
              </div>
            )}

            {/* Has logo - show upload */}
            {(hasLogo === true || (hasLogo === null && chosenPath === 'web')) && (
              <div className="space-y-4">
                {logoPreviewUrl ? (
                  <div className="flex flex-col items-center gap-3 pt-4">
                    <div className="relative w-48 h-48 rounded-xl overflow-hidden border border-gray-200 bg-white">
                      <img 
                        src={logoPreviewUrl} 
                        alt="Logo preview" 
                        className="w-full h-full object-contain p-2"
                      />
                      <button
                        type="button"
                        onClick={handleRemoveLogo}
                        className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-md hover:bg-red-50 transition-colors"
                      >
                        <Trash className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                    <p className="text-[13px] text-brand-black/50">Logo cargado correctamente</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-3 pt-6">
                    <input
                      type="file"
                      id="logo-upload"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                      disabled={uploadingLogo}
                    />
                    <label
                      htmlFor="logo-upload"
                      className={`inline-flex items-center justify-center gap-2 h-10 w-full max-w-[200px] bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md cursor-pointer transition-colors shadow-sm ${
                        uploadingLogo ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {uploadingLogo ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Subiendo...
                        </>
                      ) : (
                        <>
                          <ImagePlus className="w-5 h-5" />
                          Subir logo
                        </>
                      )}
                    </label>
                  </div>
                )}
              </div>
            )}

            {/* No logo - show generate option or generated logo preview */}
            {hasLogo === false && (
              <div className="flex flex-col items-center pt-6 gap-4">
                {generatedLogoUrl ? (
                  <div className="flex flex-col items-center gap-4">
                    <div className="relative w-48 h-48 rounded-xl overflow-hidden border border-gray-200 bg-white">
                      <img
                        src={generatedLogoUrl}
                        alt="Logo generado"
                        className="w-full h-full object-contain p-2"
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <a
                        href={generatedLogoUrl}
                        download="logo.png"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 h-10 px-4 bg-surface-card hover:bg-neutral-100 text-neutral-600 text-[15px] font-medium border border-neutral-200 rounded-md transition-colors shadow-sm"
                      >
                        <Download className="w-4 h-4" />
                        Descargar
                      </a>
                      <button
                        type="button"
                        onClick={handleGenerateLogo}
                        disabled={generatingLogo}
                        className="inline-flex items-center gap-2 h-10 px-4 bg-surface-card hover:bg-neutral-100 text-neutral-600 text-[15px] font-medium border border-neutral-200 rounded-md transition-colors shadow-sm disabled:opacity-50"
                      >
                        {generatingLogo ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4" />
                        )}
                        Regenerar
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={handleGenerateLogo}
                    disabled={generatingLogo}
                    className={`inline-flex items-center gap-2 h-10 px-6 bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md transition-colors shadow-sm ${
                      generatingLogo ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                  >
                    {generatingLogo ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Generando...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5" />
                        Generar logo con IA
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
          </div>
        );
      case 'success':
        if (creatingBrand) {
          return (
            <div className="flex flex-col items-center justify-center py-4 space-y-4">
              <Loader2 className="w-12 h-12 text-brand-primary animate-spin" />
              <div className="min-h-[48px] flex items-center justify-center">
                <p
                  key={loadingPhrase}
                  className="text-[14px] sm:text-[15px] font-medium text-brand-black/80 text-center max-w-sm transition-opacity duration-500 animate-fade-in"
                >
                  {LOADING_PHRASES[loadingPhrase]}
                </p>
              </div>
              <p className="text-[12px] text-neutral-400 text-center max-w-sm">
                No cierres esta ventana mientras procesamos tu marca.
              </p>
            </div>
          );
        }
        if (webhookError || creationTimedOut) {
          const isWeb = chosenPath === 'web';
          // Entry point del flujo manual segun el modo (additional salta el intro)
          const manualEntryStep: Step = isAdditional ? 'brand_info' : 'intro';
          const handleManualFallback = () => {
            setChosenPath('manual');
            resetCreationAndGoTo(manualEntryStep);
          };
          return (
            <div className="flex flex-col items-center justify-center py-4 space-y-5">
              <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center">
                <AlertCircle className="w-10 h-10 text-red-500" />
              </div>
              <div className="w-full flex flex-col gap-2 max-w-sm">
                {isWeb && (
                  <button
                    type="button"
                    onClick={() => resetCreationAndGoTo('web_url')}
                    className="inline-flex items-center justify-center gap-2 px-4 h-11 rounded-md bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium shadow-sm transition-colors"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Probar con otra URL
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleManualFallback}
                  className={`inline-flex items-center justify-center gap-2 px-4 h-11 rounded-md text-[15px] font-medium transition-colors ${
                    isWeb
                      ? 'border border-neutral-200 text-brand-black/80 hover:bg-neutral-50'
                      : 'bg-brand-primary hover:bg-brand-primary-hover text-white shadow-sm'
                  }`}
                >
                  <PenLine className="w-4 h-4" />
                  {isWeb ? 'Cargar los datos manualmente' : 'Reintentar manualmente'}
                </button>
              </div>
            </div>
          );
        }
        return (
          <div className="flex flex-col items-center justify-center py-4 space-y-4">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
              <Check className="w-10 h-10 text-green-500" />
            </div>
          </div>
        );
    }
  };

  // Success screen removed; we navigate to Home immediately after final step

  // Marketing plan review has been moved to MarketingPlanReview component

  const introIllustration = currentStep === 'intro' ? (
    <img
      src="https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/ilustracion-saludo.png"
      alt="Ilustración de una mujer saludando"
      className="w-full max-w-[240px] sm:max-w-[280px] mx-auto"
      loading="lazy"
    />
  ) : null;

  const isSimpleStep = currentStep === 'intro' || currentStep === 'choose_path' || currentStep === 'web_url';
  const isTransparentStep = currentStep === 'logo' || currentStep === 'success';
  // En estado de error en success, ocultamos los botones del footer porque las acciones
  // de recovery viven dentro del contenido (Probar otra URL / Cargar manual / Volver).
  const isErrorRecovery = currentStep === 'success' && (Boolean(webhookError) || creationTimedOut);

  return (
    <>
      {/* Loading Animation for logo generation - 35 seconds */}
      {generatingLogo && (
        <LoadingAnimation
          message="Generando tu logo..."
          subMessage="Esto puede tomar unos segundos"
          durationMs={35000}
        />
      )}
      
      <div className="sm:p-8 min-h-[100svh] sm:min-h-screen w-full bg-[#EFF0F5] sm:flex sm:items-center sm:justify-center">
      <div className="w-full sm:w-[720px] mx-auto">
        {/* Progress Bar (desktop only here) */}
        {/*
        <div className="hidden sm:block mt-4 mb-8">
          <div className="w-full h-1 bg-gray-200 rounded-full">
            <div className="h-1 bg-blue-500 rounded-full transition-all" style={{ width: `${progressPercent}%` }} />
          </div>
        </div>
        */}

        {/* Mobile layout: top third header, bottom two-thirds content */}
        <div className="sm:hidden">
          <div className="h-[32svh] px-4 flex flex-col items-center justify-center text-center">
            <div>
              <h2 className="text-[17px] font-semibold text-brand-black/90">{stepTexts.title}</h2>
              {stepTexts.subtitle ? (
                <p className="text-[15px] text-brand-black/50 mt-1">{stepTexts.subtitle}</p>
              ) : null}
            </div>
            {/*
            <div className="w-full max-w-[720px] mt-3">
              <div className="w-full h-1 bg-gray-200 rounded-full">
                <div className="h-1 bg-blue-500 rounded-full transition-all" style={{ width: `${progressPercent}%` }} />
              </div>
            </div>
            */}
          </div>
          
          {introIllustration && (
            <div className="px-4 mb-4 flex justify-center">
              {introIllustration}
            </div>
          )}
          <div className={`px-4 ${isSimpleStep ? 'pb-6' : 'h-[68svh]'}`}>
            <form onSubmit={(e) => { e.preventDefault(); handleNext(); }} className={isSimpleStep ? '' : 'h-full'}>
              <div className={`rounded-2xl p-4 pb-4 mb-6 flex flex-col ${isTransparentStep ? '' : 'bg-white'} ${isSimpleStep ? '' : 'h-[calc(100%-1.5rem)]'}`}>
                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-600 text-[13px] flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <p>{error}</p>
                  </div>
                )}
                <div className={isSimpleStep ? '' : 'flex-1 overflow-auto'}>
                  {renderStepContent()}
                </div>
                {!isErrorRecovery && (
                  <div className="flex items-center justify-between pt-3">
                    {currentStep !== 'success' && (
                      <button
                        type="button"
                        onClick={
                          currentStep === 'choose_path' || currentStep === 'intro'
                            ? () => navigate(-1)
                            : handleBack
                        }
                        disabled={loading || uploadingLogo}
                        className="px-4 py-2 text-neutral-600 text-[15px] font-medium hover:bg-neutral-100 rounded-md transition-colors flex items-center gap-2 disabled:opacity-50"
                      >
                        <ArrowLeft className="w-4 h-4" />
                        <span>Atrás</span>
                      </button>
                    )}
                    {currentStep === 'success' && <div />}
                    <button
                      type="submit"
                      disabled={
                        loading || uploadingLogo || creatingBrand ||
                        (currentStep === 'choose_path' && !chosenPath)
                      }
                      className={`inline-flex items-center gap-2 px-4 h-10 rounded-md shadow-sm text-[15px] font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                        currentStep === 'logo' && !logoFile && !generatedLogoUrl
                          ? 'text-neutral-600 hover:bg-neutral-100'
                          : 'bg-brand-primary hover:bg-brand-primary-hover text-white'
                      }`}
                    >
                      {loading || uploadingLogo ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>{uploadingLogo ? 'Subiendo...' : 'Guardando...'}</span>
                        </>
                      ) : (
                        <>
                          <span>{
                            currentStep === 'success' ? 'Terminar' :
                            currentStep === 'logo' ? ((logoFile || generatedLogoUrl) ? 'Siguiente' : 'Saltear paso') :
                            currentStep === 'choose_path' ? 'Continuar' :
                            currentStep === 'intro' ? 'Comenzar' : 'Siguiente'
                          }</span>
                          <ArrowRight className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>

        {/* Desktop layout */}
        <div className="hidden sm:block w-full sm:w-[720px] mx-auto">
          <div className="text-center mb-6">
            <h2 className="text-[17px] font-semibold text-brand-black/90">{stepTexts.title}</h2>
            {stepTexts.subtitle ? (
              <p className="text-[15px] text-brand-black/50 mt-1">{stepTexts.subtitle}</p>
            ) : null}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); handleNext(); }} className="space-y-6">
            {introIllustration && (
              <div className="flex justify-center">
                {introIllustration}
              </div>
            )}
            <div className={`rounded-2xl p-6 ${isTransparentStep ? '' : 'bg-white'}`}>
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-600 text-[13px] flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <p>{error}</p>
                </div>
              )}
              {renderStepContent()}
              {!isErrorRecovery && (
                <div className="flex items-center justify-between pt-6">
                  {currentStep !== 'success' && (
                    <button
                      type="button"
                      onClick={
                        currentStep === 'choose_path' || currentStep === 'intro'
                          ? () => navigate(-1)
                          : handleBack
                      }
                      disabled={loading || uploadingLogo}
                      className="px-4 py-2 text-neutral-600 text-[15px] font-medium hover:bg-neutral-100 rounded-md transition-colors flex items-center gap-2 disabled:opacity-50"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      <span>Atrás</span>
                    </button>
                  )}
                  {currentStep === 'success' && <div />}
                  <button
                    type="submit"
                    disabled={
                      loading || uploadingLogo || creatingBrand ||
                      (currentStep === 'choose_path' && !chosenPath)
                    }
                    className={`inline-flex items-center gap-2 px-4 h-10 rounded-md shadow-sm text-[15px] font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                      currentStep === 'logo' && !logoFile && !generatedLogoUrl
                        ? 'text-neutral-600 hover:bg-neutral-100'
                        : 'bg-brand-primary hover:bg-brand-primary-hover text-white'
                    }`}
                  >
                    {loading || uploadingLogo ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>{uploadingLogo ? 'Subiendo...' : 'Guardando...'}</span>
                      </>
                    ) : (
                      <>
                        <span>{
                          currentStep === 'success' ? 'Terminar' :
                          currentStep === 'logo' ? ((logoFile || generatedLogoUrl) ? 'Siguiente' : 'Saltear paso') :
                          currentStep === 'choose_path' ? 'Continuar' :
                          currentStep === 'intro' ? 'Comenzar' : 'Siguiente'
                        }</span>
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
    <SubscriptionLimitModal
      isOpen={showMultiBrandRequiredModal}
      onClose={() => {
        setShowMultiBrandRequiredModal(false);
        // Sacamos al usuario de esta ruta para que no pueda completar el flujo
        // por la URL si no tiene plan que lo habilite.
        navigate('/', { replace: true });
      }}
      type="multi-brand-required"
    />
    </>
  );
};

export default BrandDescription;